import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {equals, isNotNil} from 'ramda';

import {
  AddressRequestBodyV2,
  CreateCustomerRequestBody,
  SetPreferredLocaleRequestBody,
  useCreateCustomerAddressV2Mutation,
  useCreateCustomerV2Mutation,
  useDeleteCustomerAddressMutation,
  usePatchCustomerAddressV2Mutation,
  useSetPreferredLocaleMutation,
} from '@dms/api/customer';

import {emptyAddressPol} from '../../../constants/emptyValues';
import {handleApiError} from '../../../utils/handleApiError';
import {isAddressCompleted} from '../../../utils/isAddressCompleted';

type CreatedCustomer = {
  customerId: string;
  addressId: string | null;
};

/** The address this flow created, with the data it last saved successfully. */
type SavedAddress = {
  id: string;
  data: AddressRequestBodyV2;
};

type CachedCustomer = {
  customerId: string;
  savedAddress: SavedAddress | null;
};

type CreateCustomerWithAddressArgs = {
  createCustomerRequestBody: CreateCustomerRequestBody;
  locale: SetPreferredLocaleRequestBody['locale'];
  addressData: AddressRequestBodyV2 | null;
};

const isAddressToSave = (addressData: AddressRequestBodyV2 | null) =>
  isAddressCompleted(addressData) && isNotNil(addressData?.address);

const getAddressRequestBody = (addressData: AddressRequestBodyV2) => ({
  address: addressData.address ?? emptyAddressPol.address,
  type: null,
  invalid: null,
});

/**
 * Creates the customer, sets its locale and saves its address. The create flow is not atomic,
 * so when a later step fails and the user submits again, the customer created before is reused
 * instead of creating a duplicate. On every submit the locale is set again and the address is
 * brought to the form state: created, updated when it changed, or deleted when it was cleared.
 * A failed address step rejects, so the dialog stays open and the next submit tries it again.
 */
export function useCreateCustomerWithAddress() {
  const [createdCustomer, setCreatedCustomer] = useState<CachedCustomer | null>(null);

  const [createCustomer, {isLoading: isCreateCustomerLoading}] = useCreateCustomerV2Mutation();
  const [createCustomerAddress, {isLoading: isCreateCustomerAddressLoading}] =
    useCreateCustomerAddressV2Mutation();
  const [patchCustomerAddress, {isLoading: isPatchCustomerAddressLoading}] =
    usePatchCustomerAddressV2Mutation();
  const [deleteCustomerAddress, {isLoading: isDeleteCustomerAddressLoading}] =
    useDeleteCustomerAddressMutation();
  const [setPreferredLocale] = useSetPreferredLocaleMutation();

  /** Resolves with the address that exists for the customer after this submit. */
  const saveAddress = (
    customerId: string,
    savedAddress: SavedAddress | null,
    addressData: AddressRequestBodyV2 | null
  ): Promise<SavedAddress | null> =>
    match({savedAddress, addressData: isAddressToSave(addressData) ? addressData : null})
      .with({savedAddress: Pattern.nullish, addressData: Pattern.nullish}, () =>
        Promise.resolve(null)
      )
      .with({savedAddress: Pattern.nullish, addressData: Pattern.nonNullable}, (state) =>
        createCustomerAddress({
          customerId,
          addressRequestBody: getAddressRequestBody(state.addressData),
        })
          .unwrap()
          .then(({id}) => ({id, data: state.addressData}))
      )
      .with({savedAddress: Pattern.nonNullable, addressData: Pattern.nullish}, (state) =>
        deleteCustomerAddress({customerId, addressId: state.savedAddress.id})
          .unwrap()
          .then(() => null)
      )
      .with({savedAddress: Pattern.nonNullable, addressData: Pattern.nonNullable}, (state) =>
        equals(state.savedAddress.data, state.addressData)
          ? Promise.resolve(state.savedAddress)
          : patchCustomerAddress({
              customerId,
              addressId: state.savedAddress.id,
              addressRequestBody: getAddressRequestBody(state.addressData),
            })
              .unwrap()
              .then(() => ({id: state.savedAddress.id, data: state.addressData}))
      )
      .exhaustive();

  const getCustomer = (args: CreateCustomerWithAddressArgs): Promise<CachedCustomer> =>
    isNotNil(createdCustomer)
      ? Promise.resolve(createdCustomer)
      : createCustomer({createCustomerRequestBody: args.createCustomerRequestBody})
          .unwrap()
          .then(({id}) => {
            // Cached before the next steps, so a failure in them never creates a second customer
            const customer = {customerId: id, savedAddress: null};
            setCreatedCustomer(customer);
            return customer;
          });

  const createCustomerWithAddress = (
    args: CreateCustomerWithAddressArgs
  ): Promise<CreatedCustomer> =>
    getCustomer(args).then((customer) =>
      setPreferredLocale({
        customerId: customer.customerId,
        setPreferredLocaleRequestBody: {locale: args.locale},
      })
        .unwrap()
        .catch(handleApiError)
        .then(() => saveAddress(customer.customerId, customer.savedAddress, args.addressData))
        .then((savedAddress) => {
          setCreatedCustomer({customerId: customer.customerId, savedAddress});
          return {customerId: customer.customerId, addressId: savedAddress?.id ?? null};
        })
    );

  return {
    createCustomerWithAddress,
    isLoading:
      isCreateCustomerLoading ||
      isCreateCustomerAddressLoading ||
      isPatchCustomerAddressLoading ||
      isDeleteCustomerAddressLoading,
  };
}
